// Calculate monthly breakdown for a contract using FIFO (First In First Out) payment allocation
// This ensures payments are applied to the oldest outstanding balance first, regardless of when payment was made
export function calculateMonthlyBreakdown(contract, payments) {
    if (!contract || !contract.start_date) return [];
    
    const startDate = new Date(contract.start_date);
    if (isNaN(startDate.getTime())) return [];
    
    const now = new Date();
    const endDate = contract.end_date ? new Date(contract.end_date) : null;
    const latestDate = endDate && endDate < now ? endDate : now;
    
    const initialPayment = parseFloat(contract.initial_payment) || 0;
    const amountPerPayment = parseFloat(contract.amount_per_payment) || 0;
    const paymentDateOffset = parseInt(contract.payment_date_offset) || 0;
    
    // Step 1: Build monthly dues structure chronologically
    const monthlyDues = [];
    let currentDate = new Date(startDate);
    currentDate.setDate(1); // Start of month
    if (paymentDateOffset !== 0) {
        currentDate.setDate(currentDate.getDate() + paymentDateOffset);
    }
    
    while (currentDate <= latestDate) {
        const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        
        // Calculate amount due for this month
        let monthDue = 0;
        if (currentDate.getTime() === new Date(startDate.getFullYear(), startDate.getMonth(), 1).getTime()) {
            // First month includes initial payment
            monthDue = initialPayment + amountPerPayment;
        } else {
            monthDue = amountPerPayment;
        }
        
        monthlyDues.push({
            month: monthKey,
            monthLabel: monthLabel,
            due: monthDue,
            paid: 0, // Will be calculated using FIFO
            balance: 0, // Will be calculated
            runningBalance: 0 // Track cumulative balance
        });
        
        // Move to next month
        currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    // Step 2: Sort payments chronologically (oldest first for FIFO)
    const sortedPayments = payments
        .filter(p => p && p.date)
        .map(p => ({
            ...p,
            date: new Date(p.date),
            amount: parseFloat(p.amount) || 0
        }))
        .filter(p => !isNaN(p.date.getTime()) && p.amount > 0)
        .sort((a, b) => a.date - b.date); // Oldest first
    
    // Step 3: Apply payments using FIFO - allocate to oldest outstanding balance first
    let paymentIndex = 0;
    let remainingPayment = sortedPayments.length > 0 ? sortedPayments[0].amount : 0;
    
    for (let i = 0; i < monthlyDues.length; i++) {
        const month = monthlyDues[i];
        
        // Calculate running balance (cumulative due minus cumulative paid)
        const previousBalance = i > 0 ? monthlyDues[i - 1].runningBalance : 0;
        month.runningBalance = previousBalance + month.due;
        
        // Apply remaining payment to this month's balance
        while (remainingPayment > 0 && month.runningBalance > 0) {
            const amountToApply = Math.min(remainingPayment, month.runningBalance);
            month.paid += amountToApply;
            month.runningBalance -= amountToApply;
            remainingPayment -= amountToApply;
            
            // Move to next payment if current one is exhausted
            if (remainingPayment <= 0 && paymentIndex < sortedPayments.length - 1) {
                paymentIndex++;
                remainingPayment = sortedPayments[paymentIndex].amount;
            }
        }
        
        // Calculate this month's balance (paid - due)
        month.balance = month.paid - month.due;
    }
    
    return monthlyDues;
}

export function appendContractBalances(contracts, payments) {
    if (!contracts || !Array.isArray(contracts)) {
        return [];
    }
    if (!payments || !Array.isArray(payments)) payments = [];

    // Get a list of all relevant payments for each contract for quick lookup
    const contractPaymentMap = {};
    payments.forEach(p => {
        const contractId = parseInt(p.contract);
        if (!contractPaymentMap[contractId]) contractPaymentMap[contractId] = [p];
        else contractPaymentMap[contractId].push(p);
    });

    const now = new Date();
    return contracts.map(c => {
        // Initialize with safe defaults - always ensure these are numbers
        const initialPayment = parseInt(c.initial_payment) || 0;
        const amountPerPayment = parseInt(c.amount_per_payment) || 0;
        const paymentDateOffset = parseInt(c.payment_date_offset) || 0;

        // Always explicitly initialize both balance values to 0 first
        c.balanceOwed = 0;
        c.balancePaid = 0;

        // Start balanceOwed with initial payment
        c.balanceOwed = initialPayment;

        // Calculate amount that should be paid
        if (c.payment_frequency === 'One Time') {
            c.balanceOwed += amountPerPayment;
        } else if (c.payment_frequency === 'Monthly' && c.start_date) {
            try {
                const start = new Date(c.start_date);
                if (isNaN(start.getTime())) {
                    c.balanceOwed = initialPayment;
                } else {
                    start.setDate(start.getDate() + paymentDateOffset);
                    let latestDate = now;
                    if (c.end_date) {
                        const end = new Date(c.end_date);
                        if (!isNaN(end.getTime())) {
                            latestDate = now > end ? end : now;
                        }
                    }
                    // Ensure latestDate is a valid Date object
                    if (latestDate && typeof latestDate.getFullYear === 'function') {
                        let monthsDiff =
                            (latestDate.getFullYear() - start.getFullYear()) * 12 +
                            (latestDate.getMonth() - start.getMonth());
                        if (latestDate.getDate() >= start.getDate()) monthsDiff += 1;

                        c.balanceOwed += monthsDiff * amountPerPayment;
                    } else {
                        c.balanceOwed = initialPayment;
                    }
                }
            } catch (error) {
                c.balanceOwed = initialPayment;
            }
        } else if (c.payment_frequency === 'Weekly' && c.start_date) {
            try {
                const start = new Date(c.start_date);
                if (isNaN(start.getTime())) {
                    c.balanceOwed = initialPayment;
                } else {
                    start.setDate(start.getDate() + paymentDateOffset);
                    let latestDate = now;
                    if (c.end_date) {
                        const end = new Date(c.end_date);
                        if (!isNaN(end.getTime())) {
                            latestDate = now > end ? end : now;
                        }
                    }
                    // Ensure latestDate is a valid Date object
                    if (latestDate && typeof latestDate.getTime === 'function') {
                        let weeksDiff = Math.ceil(((latestDate - start) / 7) / (1000 * 60 * 60 * 24));

                        c.balanceOwed += weeksDiff * amountPerPayment;
                    } else {
                        c.balanceOwed = initialPayment;
                    }
                }
            } catch (error) {
                c.balanceOwed = initialPayment;
            }
        } else if (c.payment_frequency && c.payment_frequency !== 'One Time') {
            c.balanceOwed = 100000; // Unknown frequency
        }

        // Calculate the amount that has been paid
        const relPayments = contractPaymentMap[parseInt(c.id)] || [];
        let newBalance = 0;
        relPayments.forEach(p => {
            const amount = parseInt(p.amount) || 0;
            newBalance += amount;
        });

        // Always explicitly set both balance values (never undefined or null)
        c.balancePaid = newBalance || 0;
        // Ensure balanceOwed is always a number (never undefined or null)
        if (c.balanceOwed === undefined || c.balanceOwed === null || isNaN(c.balanceOwed)) {
            c.balanceOwed = initialPayment || 0;
        }
        // Final safety check - ensure both are numbers
        c.balanceOwed = Number(c.balanceOwed) || 0;
        c.balancePaid = Number(c.balancePaid) || 0;

        c.relevantPayments = relPayments.sort((a, b) => {
            let aDate = new Date(a.date);
            let bDate = new Date(b.date);
            return bDate - aDate;
        });
        
        // Calculate monthly breakdown for hover tooltip
        c.monthlyBreakdown = calculateMonthlyBreakdown(c, relPayments);

        return c;
    });

    return contracts;
}